/**
 * Utility functions for processing blog content
 */

/**
 * Adds ID attributes to headings in HTML content
 * @param content - HTML content string
 * @returns HTML content with ID attributes added to headings
 */
export const addHeadingIds = (content: string): string => {
  return content.replace(
    /<h([2-4])([^>]*)>(.*?)<\/h[2-4]>/gi,
    (match, level, attributes, text) => {
      // Extract text content and create ID
      const textContent = text.replace(/<[^>]*>/g, '').trim();
      const id = textContent
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      
      // Check if ID already exists
      if (attributes.includes('id=')) {
        return match; // Don't modify if ID already exists
      }
      
      // Add ID attribute
      return `<h${level}${attributes} id="${id}">${text}</h${level}>`;
    }
  );
};

/**
 * Processes blog content to ensure proper heading IDs
 * @param content - Raw blog content
 * @returns Processed content with heading IDs
 */
export const processBlogContent = (content: string): string => {
  const withIds = addHeadingIds(content);
  return addLazyLoadingToImages(withIds);
};

/**
 * Adds loading and decoding attributes to images for better performance
 */
export const addLazyLoadingToImages = (content: string): string => {
  // Add loading="lazy" and decoding="async" to <img> tags that don't already have them
  return content.replace(/<img(?![^>]*\bloading=)[^>]*>/gi, (match) => {
    let updated = match;
    // Ensure loading="lazy"
    updated = updated.replace(/<img/i, '<img loading="lazy"');
    // Ensure decoding="async" (add if missing)
    if (!/\bdecoding=/.test(updated)) {
      updated = updated.replace(/<img(\s[^>]*)?>/i, (m, attrs = '') => `<img decoding="async"${attrs || ''}>`);
    }
    return updated;
  });
};
